import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatListItem, MatListItemIcon, MatListItemTitle, MatListModule } from '@angular/material/list';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { MenuItem } from './admin-sidenav.component';

@Component({
  selector: 'buspro-admin-menu-item',
  standalone: true,
  imports: [
    MatIcon,
    MatListItem,
    MatListItemIcon,
    MatListItemTitle,
    MatListModule,
    MatIconModule,
    MatBadgeModule,
    RouterLink,
    RouterLinkActive,
  ],
  animations: [
    trigger('expandContractMenu', [
      transition(':enter', [
        style({ opacity: 0, height: '0px' }),
        animate('500ms ease-in-out', style({ opacity: 1, height: '*' })),
      ]),
      transition(':leave', [animate('500ms ease-in-out', style({ opacity: 0, height: '0px' }))]),
    ]),
  ],
  template: `
    <a
      mat-list-item
      class="menu-item"
      (click)="onMenuItemClick($event)"
      [routerLink]="item().route"
      routerLinkActive="selected-menu-item"
      #rla="routerLinkActive"
      [activated]="rla.isActive"
    >
      <mat-icon
        [fontSet]="rla.isActive ? 'material-icons' : 'material-icons-outlined'"
        matListItemIcon
        [matBadge]="collapsed() && shouldShowBadge(item().badge) ? item().badge : ''"
        [matBadgeHidden]="!collapsed() || !shouldShowBadge(item().badge)"
        matBadgeSize="small"
        matBadgeColor="warn"
        >{{ item().icon }}
      </mat-icon>
      @if (!collapsed()) {
      <span matListItemTitle class="flex items-center gap-2">
        {{ item().label }}
        @if (shouldShowBadge(item().badge) && !nestedMenuOpen()) {
        <span class="badge badge-warning badge-sm">{{ item().badge }}</span>
        }
      </span>
      } @if (item().subItems) {
      <span matListItemMeta>
        @if (nestedMenuOpen()) {
        <mat-icon>expand_less</mat-icon>
        } @else {
        <mat-icon>expand_more</mat-icon>
        }
      </span>
      }
    </a>
    @if (item().subItems && nestedMenuOpen()) {
    <div @expandContractMenu>
      @for (subItem of item().subItems; track $index) {
      <a
        mat-list-item
        class="menu-item "
        [class.indented]="!collapsed()"
        [routerLink]="item().route + '/' + subItem.route"
        routerLinkActive
        #rla="routerLinkActive"
        [activated]="rla.isActive"
      >
        <mat-icon
          [fontSet]="
            rla.isActive ? 'material-icons' : 'material-icons-outlined'
          "
          matListItemIcon
          [matBadge]="collapsed() && shouldShowBadge(subItem.badge) ? subItem.badge : ''"
          [matBadgeHidden]="!collapsed() || !shouldShowBadge(subItem.badge)"
          matBadgeSize="small"
          matBadgeColor="warn"
          >{{ subItem.icon }}
        </mat-icon>
        @if (!collapsed()) {
        <span matListItemTitle class="flex items-center gap-2">
          {{ subItem.label }}
          @if (shouldShowBadge(subItem.badge)) {
          <span class="badge badge-warning badge-sm">{{ subItem.badge }}</span>
          }
        </span>
        }
      </a>
      }
    </div>
    }
  `,
  styles: `
    .indented {
      --mat-list-list-item-leading-icon-start-space: 48px;
    }

    .menu-item {
      border-left: 5px solid;
      border-left-color: rgba(0, 0, 0, 0);
      margin: 4px 0;
    }

    .selected-menu-item {
      border-left-color: var(--primary-color);
      background: rgba(0, 0, 0, 0.05)
    }

    :host * {
      transition: all 500ms ease-in-out;
    }
  `,
})
export class AdminMenuItemComponent {
  private router = inject(Router);
  item = input.required<MenuItem>();
  collapsed = input(false);

  nestedMenuOpen = signal(false);

  constructor() {
    // Auto-expand menu if any submenu item is active
    effect(() => {
      const menuItem = this.item();
      if (menuItem.subItems && menuItem.subItems.length > 0) {
        const currentUrl = this.router.url;
        const isAnySubItemActive = menuItem.subItems.some(
          (subItem) =>
            currentUrl.startsWith(`${menuItem.route}/${subItem.route}`) ||
            currentUrl === `${menuItem.route}/${subItem.route}`,
        );
        if (isAnySubItemActive) {
          this.nestedMenuOpen.set(true);
        }
      }
    });

    // Listen to route changes to auto-expand/update menu state
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const menuItem = this.item();
      if (menuItem.subItems && menuItem.subItems.length > 0) {
        const currentUrl = this.router.url;
        const isAnySubItemActive = menuItem.subItems.some(
          (subItem) =>
            currentUrl.startsWith(`${menuItem.route}/${subItem.route}`) ||
            currentUrl === `${menuItem.route}/${subItem.route}`,
        );
        if (isAnySubItemActive) {
          this.nestedMenuOpen.set(true);
        }
      }
    });
  }

  // Check if parent menu should be active (when any submenu is active)
  isParentActive(): boolean {
    const menuItem = this.item();
    if (!menuItem.subItems || menuItem.subItems.length === 0) {
      // For items without subItems, use routerLinkActive
      return this.router.url === menuItem.route || this.router.url.startsWith(menuItem.route + '/');
    }

    // For items with subItems, check if any submenu is active
    const currentUrl = this.router.url;
    return menuItem.subItems.some(
      (subItem) =>
        currentUrl === `${menuItem.route}/${subItem.route}` ||
        currentUrl.startsWith(`${menuItem.route}/${subItem.route}/`),
    );
  }

  onMenuItemClick(event: Event): void {
    const menuItem = this.item();

    // If item has subItems, handle navigation based on the menu type
    if (menuItem.subItems && menuItem.subItems.length > 0) {
      event.preventDefault(); // Prevent default routerLink navigation

      // For Bus Stops menu, navigate to the first subItem (overview) when clicking parent
      if (menuItem.route === '/bus-stops' && menuItem.subItems.length > 0) {
        const firstSubItem = menuItem.subItems[0];
        this.router.navigateByUrl(`${menuItem.route}/${firstSubItem.route}`);
      } else if (menuItem.route) {
        // For other menus with routes (like Audits), navigate to the route
        this.router.navigateByUrl(menuItem.route);
      }

      // Toggle the menu to show subItems
      this.nestedMenuOpen.set(!this.nestedMenuOpen());
    }
    // If no subItems, let routerLink handle navigation
  }

  shouldShowBadge(badge: string | number | undefined): boolean {
    if (badge == null) return false;
    if (typeof badge === 'number') return badge > 0;
    if (typeof badge === 'string') return badge !== '';
    return false;
  }
}
